import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  type User as FirebaseUser,
  type MultiFactorResolver,
  type MultiFactorError,
} from 'firebase/auth'
import { doc, getDoc, collection, query, where, getDocs, enableNetwork, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase/config'
import type { AppPermission } from '@/lib/auth/permissions'
import { resolvePermissions } from '@/lib/auth/permissions'
import { logUserLogin, logUserLogout } from '@/services/audit'

// Helper function to retry Firestore operations with exponential backoff
async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Re-enable network on retry attempts
      if (attempt > 0) {
        try {
          await enableNetwork(db)
        } catch {
          // Ignore if already enabled
        }
      }
      return await operation()
    } catch (error) {
      lastError = error as Error
      const isOfflineError = (error as Error).message?.includes('offline') ||
                             (error as Error).message?.includes('network')

      if (!isOfflineError || attempt === maxRetries - 1) {
        throw error
      }

      // Wait before retrying with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt)
      console.log(`Firestore offline, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw lastError
}

function isPermissionDeniedError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'code' in error &&
      (error as { code?: string }).code === 'permission-denied'
  )
}

// User role type
export type UserRole = 'admin' | 'supervisor' | 'caregiver' | 'billing_specialist' | 'qa_officer' | 'family'

// Notification preferences type
export interface NotificationPreferences {
  email?: boolean
  sms?: boolean
  push?: boolean
  scheduleReminders?: boolean
  shiftUpdates?: boolean
  newMessages?: boolean
  openShiftAlerts?: boolean
}

// Extended user type with our app data
export interface AppUser {
  uid: string
  id: string // Firestore document ID (may differ from uid for invited users)
  email: string | null
  agencyId: string
  agencyName: string
  role: UserRole
  primaryRole: UserRole
  permissions: AppPermission[]
  firstName: string
  lastName: string
  phone?: string
  photoURL?: string
  themePreference?: 'light' | 'dark' | 'system'
  notificationPreferences?: NotificationPreferences
  mfaEnabled?: boolean
  status: 'active' | 'inactive' | 'pending_verification' | 'suspended'
  createdAt?: import('firebase/firestore').Timestamp | Date | string
  skills?: string[]
  certifications?: string[]
  position?: string
  customPosition?: string
  isAgencyOwner?: boolean
  // Home address (for caregivers)
  homeAddress?: {
    street?: string
    city?: string
    state?: string
    zip?: string
    coordinates?: {
      latitude: number
      longitude: number
    }
  }
  // Scheduling preferences (for caregivers)
  schedulingPreferences?: {
    preferredClients?: string[]
    blacklistedClients?: string[]
    preferredShiftTimes?: ('morning' | 'afternoon' | 'evening' | 'overnight')[]
    preferredServiceTypes?: string[]
    preferredZipCodes?: string[]
    maxHoursPerWeek?: number
    maxHoursPerDay?: number
  }
}

// MFA required error with resolver
export interface MFARequiredError extends Error {
  code: 'auth/multi-factor-auth-required'
  resolver: MultiFactorResolver
}

interface AuthContextValue {
  user: AppUser | null
  firebaseUser: FirebaseUser | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<AppUser>
  signInWithMFA: (resolver: MultiFactorResolver, verificationCode: string) => Promise<AppUser>
  signUp: (email: string, password: string) => Promise<FirebaseUser>
  signOut: () => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  refreshUser: (fbUser?: FirebaseUser) => Promise<void>
  agencyData: {
    id: string
    name: string
    address?: { street?: string; city?: string; state?: string; zip?: string }
    phone?: string
    email?: string
    [key: string]: unknown
  } | null
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser] = useState<AppUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Helper function to fetch user data (defined early for use in useEffect)
  const fetchUserDataInternal = async (firebaseUser: FirebaseUser): Promise<AppUser | null> => {
    try {
      return await withRetry(async () => {
        // First try to get user by document ID (for admin users created during signup)
        let userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))

        // If not found, query by uid field (for invited users whose doc ID doesn't match Auth UID)
        if (!userDoc.exists()) {
          const usersQuery = query(
            collection(db, 'users'),
            where('uid', '==', firebaseUser.uid)
          )
          const querySnapshot = await getDocs(usersQuery)
          if (!querySnapshot.empty) {
            userDoc = querySnapshot.docs[0]

            // Migrate: copy doc to users/{authUid} so Firestore security rules work
            // (rules use get() by doc ID, not query by uid field)
            if (userDoc.id !== firebaseUser.uid) {
              try {
                const { setDoc } = await import('firebase/firestore')
                const oldData = userDoc.data()
                await setDoc(doc(db, 'users', firebaseUser.uid), {
                  ...oldData,
                  uid: firebaseUser.uid,
                }, { merge: true })
                // Re-fetch from new location
                userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
                console.log(`Migrated user doc from old ID to users/${firebaseUser.uid}`)
              } catch (migrateErr) {
                console.error('Failed to migrate user doc:', migrateErr)
                // Continue with existing doc - reads will work, writes may fail
              }
            }
          }
        }

        if (userDoc.exists()) {
          const userData = userDoc.data()

          // Fetch agency name
          let agencyName = 'Unknown Agency'
          if (userData.agencyId) {
            try {
              const agencyDoc = await getDoc(doc(db, 'agencies', userData.agencyId))
              if (agencyDoc.exists()) {
                const agencyData = agencyDoc.data()
                agencyName = agencyData?.name || 'Unnamed Agency'
              } else {
                console.warn(`Agency document not found for agencyId: ${userData.agencyId}`)
              }
            } catch (agencyError) {
              console.error('Error fetching agency:', agencyError)
              agencyName = 'Agency (Error)'
            }
          } else {
            console.warn('User has no agencyId')
          }

          // If Firebase Auth email differs from Firestore email, sync it
          // This happens after email verification completes
          if (firebaseUser.email && userData.email !== firebaseUser.email) {
            try {
              await updateDoc(doc(db, 'users', userDoc.id), {
                email: firebaseUser.email,
                pendingEmail: null, // Clear pending email since it's now verified
                updatedAt: serverTimestamp(),
              })
              console.log('Synced email from Firebase Auth to Firestore')
            } catch (syncError) {
              console.error('Failed to sync email to Firestore:', syncError)
            }
          }

          const primaryRole = (userData.primaryRole || userData.role) as UserRole
          const permissions = resolvePermissions(primaryRole, userData.permissions as AppPermission[] | undefined)

          return {
            uid: firebaseUser.uid,
            id: userDoc.id, // Firestore document ID
            email: firebaseUser.email,
            agencyId: userData.agencyId,
            agencyName,
            role: userData.role,
            primaryRole,
            permissions,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phone: userData.phone || undefined,
            photoURL: userData.avatarUrl || userData.photoURL || undefined,
            themePreference: userData.themePreference || undefined,
            notificationPreferences: userData.notificationPreferences || undefined,
            mfaEnabled: userData.mfaEnabled || false,
            status: userData.status,
            skills: userData.skills || undefined,
            certifications: userData.certifications || undefined,
            position: userData.position || undefined,
            customPosition: userData.customPosition || undefined,
            isAgencyOwner: userData.isAgencyOwner === true || userData.agencyId === firebaseUser.uid,
            homeAddress: userData.homeAddress || undefined,
            schedulingPreferences: userData.schedulingPreferences || undefined,
          }
        }
        return null
      })
    } catch (error) {
      console.error('Error fetching user data:', error)
      return null
    }
  }

  // Listen for auth state changes
  useEffect(() => {
    let authResolved = false

    // Safety timeout: if onAuthStateChanged never fires (e.g. Firebase can't
    // reach the server to validate a persisted token), stop the spinner after
    // 10 seconds so the user isn't stuck forever.
    const timeout = setTimeout(() => {
      if (!authResolved) {
        console.warn('Auth state listener timed out — treating as unauthenticated')
        setUser(null)
        setIsLoading(false)
      }
    }, 10_000)

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      authResolved = true
      clearTimeout(timeout)
      setFirebaseUser(firebaseUser)

      if (firebaseUser) {
        try {
          const userData = await fetchUserDataInternal(firebaseUser)
          if (userData) {
            setUser(userData)
          } else {
            console.error('User document not found in Firestore')
            setUser(null)
          }
        } catch (error) {
          console.error('Error fetching user data:', error)
          setUser(null)
        }
      } else {
        setUser(null)
      }

      setIsLoading(false)
    })

    return () => {
      clearTimeout(timeout)
      unsubscribe()
    }
  }, [])

  // ============================================
  // Session idle timeout (HIPAA requirement)
  // Sign out after 30 minutes of inactivity.
  // Family portal users are exempt (longer sessions).
  // ============================================
  const IDLE_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
  const ACTIVITY_THROTTLE_MS = 60 * 1000 // Update at most every 60 seconds
  const lastActivityRef = useRef<number>(Date.now())
  const lastActivityUpdateRef = useRef<number>(Date.now())

  const updateLastActivity = useCallback(() => {
    const now = Date.now()
    // Throttle: only update if enough time has elapsed since last update
    if (now - lastActivityUpdateRef.current >= ACTIVITY_THROTTLE_MS) {
      lastActivityRef.current = now
      lastActivityUpdateRef.current = now
    }
  }, [])

  const recordLoginAudit = async (userData: AppUser) => {
    try {
      await updateDoc(doc(db, 'users', userData.id), {
        lastLoginAt: serverTimestamp(),
      })
    } catch (error) {
      if (userData.role !== 'family' || !isPermissionDeniedError(error)) {
        console.error('Failed to update last login timestamp:', error)
      }
    }

    try {
      if (userData.role !== 'family') {
        await logUserLogin({
          agencyId: userData.agencyId,
          userId: userData.uid,
          userEmail: userData.email || '',
          userRole: userData.primaryRole || userData.role,
          userAgent: window.navigator.userAgent,
        })
      }
    } catch (error) {
      console.error('Failed to record login audit:', error)
    }
  }

  const recordLogoutAudit = async (userData: AppUser) => {
    if (userData.role === 'family') return

    try {
      await logUserLogout({
        agencyId: userData.agencyId,
        userId: userData.uid,
        userEmail: userData.email || '',
        userRole: userData.primaryRole || userData.role,
      })
    } catch (error) {
      console.error('Failed to record logout audit:', error)
    }
  }

  useEffect(() => {
    // Only run idle timeout for authenticated non-family users
    if (!user || user.role === 'family') return

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']

    // Register activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateLastActivity, { passive: true })
    })

    // Check idle time every 60 seconds
    const intervalId = setInterval(async () => {
      const idleTime = Date.now() - lastActivityRef.current
      if (idleTime >= IDLE_TIMEOUT_MS) {
        console.warn('Session timed out due to inactivity')
        await recordLogoutAudit(user)
        await firebaseSignOut(auth)
        setUser(null)
        // Navigate to login — use window.location since we don't have router access here
        window.location.href = '/login?reason=timeout'
      }
    }, 60_000)

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateLastActivity)
      })
      clearInterval(intervalId)
    }
  }, [user, updateLastActivity])

  const signIn = async (email: string, password: string, rememberMe = true): Promise<AppUser> => {
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence)
      const result = await signInWithEmailAndPassword(auth, email, password)
      const userData = await fetchUserDataInternal(result.user)
      if (!userData) {
        throw new Error('User data not found')
      }

      // Block inactive users at sign-in
      if (userData.status === 'inactive') {
        await firebaseSignOut(auth)
        throw new Error('Your account has been deactivated. Please contact your administrator.')
      }

      setFirebaseUser(result.user)
      setUser(userData)
      await recordLoginAudit(userData)
      return userData
    } catch (error) {
      const firebaseError = error as { code?: string }

      // Check if MFA is required
      if (firebaseError.code === 'auth/multi-factor-auth-required') {
        const mfaError = error as MultiFactorError
        const resolver = getMultiFactorResolver(auth, mfaError)

        // Create and throw a custom error with the resolver
        const mfaRequiredError = new Error('MFA required') as MFARequiredError
        mfaRequiredError.code = 'auth/multi-factor-auth-required'
        mfaRequiredError.resolver = resolver
        throw mfaRequiredError
      }

      throw error
    }
  }

  const signInWithMFA = async (resolver: MultiFactorResolver, verificationCode: string): Promise<AppUser> => {
    // Get the first enrolled factor (we only support one TOTP factor)
    const hint = resolver.hints[0]

    // Create the TOTP assertion
    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      hint.uid,
      verificationCode
    )

    // Complete the sign-in
    const result = await resolver.resolveSignIn(multiFactorAssertion)

    const userData = await fetchUserDataInternal(result.user)
    if (!userData) {
      throw new Error('User data not found')
    }
    setFirebaseUser(result.user)
    setUser(userData)
    await recordLoginAudit(userData)
    return userData
  }

  const signUp = async (email: string, password: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    return result.user
  }

  const signOut = async () => {
    if (user) {
      await recordLogoutAudit(user)
    }
    await firebaseSignOut(auth)
    setUser(null)
  }

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email)
  }

  const refreshUser = async (fbUser?: FirebaseUser) => {
    const userToRefresh = fbUser || firebaseUser
    if (userToRefresh) {
      const userData = await fetchUserDataInternal(userToRefresh)
      if (userData) {
        setFirebaseUser(userToRefresh)
        setUser(userData)
      }
    }
  }

  const value: AuthContextValue = {
    user,
    firebaseUser,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signInWithMFA,
    signUp,
    signOut,
    logout: signOut,
    resetPassword,
    refreshUser,
    agencyData: user ? { id: user.agencyId, name: user.agencyName } : null,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
